import { makeAutoObservable, runInAction } from 'mobx'
import organizationProfileApi from '../tenancy/organizationProfileApi'
import organizationSessionStore from './organizationSessionStore'
import { registerTenantScopedReset } from '../tenancy/tenantScopedState'
import {
	discoveryResultToOrganizationTarget,
	normalizeExactDiscoveryEmail,
	normalizeExactReldoId,
	safeProfileError,
} from '../tenancy/organizationProfilePolicy'

export class OrganizationProfileStore {
	profile = null
	discoveryResult = null
	selectedOrganizationTarget = null
	loading = false
	saving = false
	discovering = false
	error = null

	constructor({ api = organizationProfileApi, session = organizationSessionStore } = {}) {
		this.api = api
		this.session = session
		makeAutoObservable(this, { api: false, session: false }, { autoBind: true })
	}

	async load() {
		if (!this.session.isReady || this.loading) return null
		const generation = this.session.contextGeneration
		this.loading = true
		this.error = null
		try {
			const profile = await this.api.getProfile()
			if (!this.isCurrent(generation)) return null
			runInAction(() => { this.profile = profile })
			return profile
		} catch (error) {
			if (this.isCurrent(generation)) runInAction(() => { this.error = safeProfileError(error) })
			return null
		} finally {
			if (this.isCurrent(generation)) runInAction(() => { this.loading = false })
		}
	}

	async updateOrganization(patch) {
		return this.save(() => this.api.updateOrganization(patch))
	}

	async updateMembership(membershipId, patch) {
		return this.save(() => this.api.updateMembership(membershipId, patch))
	}

	async updateVisibilityPolicies(patch) {
		if (this.saving) return null
		const generation = this.session.contextGeneration
		this.saving = true
		this.error = null
		try {
			await this.api.updateVisibilityPolicies(patch)
			if (!this.isCurrent(generation)) return null
			// A policy change is a server authorization boundary.  Refreshing the
			// selected context clears tenant state before the new scoped DTOs load.
			await this.session.refreshOrganizations()
			return this.session.currentContext?.policies || null
		} catch (error) {
			if (this.isCurrent(generation)) runInAction(() => { this.error = safeProfileError(error) })
			if (error?.response?.status === 403) await this.session.refreshOrganizations()
			return null
		} finally {
			if (this.isCurrent(generation)) runInAction(() => { this.saving = false })
		}
	}

	async save(command) {
		if (this.saving) return null
		const generation = this.session.contextGeneration
		this.saving = true
		this.error = null
		try {
			const profile = await command()
			if (!this.isCurrent(generation)) return null
			runInAction(() => { this.profile = profile })
			await this.session.refreshOrganizationList()
			return profile
		} catch (error) {
			if (this.isCurrent(generation)) runInAction(() => { this.error = safeProfileError(error) })
			if (error?.response?.status === 403) await this.session.refreshOrganizations()
			return null
		} finally {
			if (this.isCurrent(generation)) runInAction(() => { this.saving = false })
		}
	}

	async discover(kind, value) {
		if (this.discovering) return null
		const normalized = kind === 'reldoId'
			? normalizeExactReldoId(value)
			: normalizeExactDiscoveryEmail(value)
		if (!normalized) {
			this.error = 'Введите точное корректное значение для поиска.'
			return null
		}
		const generation = this.session.contextGeneration
		this.discovering = true
		this.error = null
		this.discoveryResult = null
		this.selectedOrganizationTarget = null
		try {
			const result = kind === 'reldoId'
				? await this.api.discoverByReldoId(normalized)
				: await this.api.discoverByEmail(normalized)
			if (!this.isCurrent(generation)) return null
			runInAction(() => {
				this.discoveryResult = result
				this.selectedOrganizationTarget = discoveryResultToOrganizationTarget(result)
			})
			return result
		} catch (error) {
			if (this.isCurrent(generation)) runInAction(() => { this.error = safeProfileError(error) })
			return null
		} finally {
			if (this.isCurrent(generation)) runInAction(() => { this.discovering = false })
		}
	}

	clearDiscovery() {
		this.discoveryResult = null
		this.selectedOrganizationTarget = null
		this.error = null
	}

	resetTenantUi() {
		this.profile = null
		this.discoveryResult = null
		this.selectedOrganizationTarget = null
		this.loading = false
		this.saving = false
		this.discovering = false
		this.error = null
	}

	isCurrent(generation) {
		return generation === this.session.contextGeneration
	}
}

const organizationProfileStore = new OrganizationProfileStore()
registerTenantScopedReset('organization-profile-ui', organizationProfileStore.resetTenantUi)

export default organizationProfileStore
